import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  Edit3,
  KeyRound,
  Plus,
  Power,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserX,
  X
} from 'lucide-react';

import {
  useSettings
} from '../contexts/SettingsContext';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

const FORM_INICIAL = {
  nome: '',
  usuario: '',
  email: '',
  senha: '',
  perfil: 'operador'
};

export default function PainelUsers() {
  const { darkMode } = useSettings();

  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [form, setForm] = useState(FORM_INICIAL);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] =
    useState(false);

  const bg = darkMode
    ? 'bg-gray-900'
    : 'bg-gray-100';

  const card = darkMode
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';

  const text = darkMode
    ? 'text-white'
    : 'text-gray-900';

  const muted = darkMode
    ? 'text-gray-400'
    : 'text-gray-600';

  const carregarUsuarios = useCallback(async () => {
    setLoading(true);
    setErro('');

    try {
      const response = await fetch(
        `${API}/painel-users`, {
        credentials: 'include',}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
          'Erro ao carregar usuários'
        );
      }

      setUsuarios(data.data || []);
    } catch (error) {
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  const limparFormulario = () => {
    setForm(FORM_INICIAL);
    setEditando(null);
    setMostrarFormulario(false);
  };

  const abrirNovo = () => {
    setForm(FORM_INICIAL);
    setEditando(null);
    setMostrarFormulario(true);
    setErro('');
    setMensagem('');
  };

  const abrirEdicao = usuario => {
    setForm({
      nome: usuario.nome || '',
      usuario: usuario.usuario || '',
      email: usuario.email || '',
      senha: '',
      perfil: usuario.perfil || 'operador'
    });

    setEditando(usuario);
    setMostrarFormulario(true);
    setErro('');
    setMensagem('');
  };

  const salvarUsuario = async event => {
    event.preventDefault();
    setSalvando(true);
    setErro('');
    setMensagem('');

    try {
      const endpoint = editando
        ? `${API}/painel-users/${editando.id}`
        : `${API}/painel-users`;

      const response = await fetch(endpoint, {
        credentials: 'include',method: editando ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
          'Não foi possível salvar o usuário'
        );
      }

      setMensagem(
        editando
          ? 'Usuário atualizado com sucesso.'
          : 'Usuário criado com sucesso.'
      );

      limparFormulario();
      await carregarUsuarios();
    } catch (error) {
      setErro(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const trocarSenha = async usuario => {
    const senha = window.prompt(
      `Digite a nova senha de ${usuario.nome}:`
    );

    if (!senha) return;

    if (senha.length < 8) {
      window.alert(
        'A senha deve ter no mínimo 8 caracteres.'
      );
      return;
    }

    try {
      const response = await fetch(
        `${API}/painel-users/${usuario.id}/senha`, {
        credentials: 'include',method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ senha })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
          'Erro ao alterar senha'
        );
      }

      setMensagem(
        `Senha de ${usuario.nome} alterada.`
      );
    } catch (error) {
      setErro(error.message);
    }
  };

  const alterarStatus = async usuario => {
    const novoStatus = !usuario.ativo;

    const acao = novoStatus
      ? 'reativar'
      : 'desativar';

    if (!window.confirm(
      `Confirma ${acao} o acesso de ${usuario.nome}?`
    )) {
      return;
    }

    try {
      const response = await fetch(
        `${API}/painel-users/${usuario.id}/status`, {
        credentials: 'include',method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ativo: novoStatus
          })
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
          `Erro ao ${acao} usuário`
        );
      }

      setMensagem(
        novoStatus
          ? 'Usuário reativado com sucesso.'
          : 'Acesso desativado imediatamente.'
      );

      await carregarUsuarios();
    } catch (error) {
      setErro(error.message);
    }
  };

  const excluirUsuario = async usuario => {
    if (!window.confirm(
      `EXCLUIR definitivamente o usuário "${usuario.nome}"?\n\nPara preservar o histórico, prefira desativar o acesso.`
    )) {
      return;
    }

    try {
      const response = await fetch(
        `${API}/painel-users/${usuario.id}`, {
        credentials: 'include',method: 'DELETE',}
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
          'Erro ao excluir usuário'
        );
      }

      setMensagem(
        'Usuário excluído definitivamente.'
      );

      await carregarUsuarios();
    } catch (error) {
      setErro(error.message);
    }
  };

  if (loading) {
    return (
      <div className={`${bg} min-h-screen flex items-center justify-center`}>
        <RefreshCw
          size={30}
          className="animate-spin text-blue-600"
        />
      </div>
    );
  }

  return (
    <div className={`${bg} min-h-screen space-y-6`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-blue-600 text-white">
              <ShieldCheck size={24} />
            </div>

            <div>
              <h1 className={`${text} text-2xl font-bold`}>
                Usuários do Painel
              </h1>

              <p className={`${muted} text-sm mt-1`}>
                Cadastro, permissões e controle de acesso
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={abrirNovo}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
        >
          <Plus size={18} />
          Novo usuário
        </button>
      </div>

      {erro && (
        <div className="px-4 py-3 rounded-lg border border-red-800 bg-red-950/40 text-red-300">
          {erro}
        </div>
      )}

      {mensagem && (
        <div className="px-4 py-3 rounded-lg border border-green-800 bg-green-950/40 text-green-300">
          {mensagem}
        </div>
      )}

      {mostrarFormulario && (
        <form
          onSubmit={salvarUsuario}
          className={`${card} border rounded-xl p-6 shadow-sm`}
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className={`${text} text-lg font-semibold`}>
              {editando
                ? 'Editar usuário'
                : 'Cadastrar novo usuário'}
            </h2>

            <button
              type="button"
              onClick={limparFormulario}
              className={`${muted} hover:text-red-500`}
            >
              <X size={21} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                Nome completo
              </label>

              <input
                required
                value={form.nome}
                onChange={event =>
                  setForm({
                    ...form,
                    nome: event.target.value
                  })
                }
                className={`${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } w-full border rounded-lg px-4 py-2.5`}
              />
            </div>

            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                Usuário
              </label>

              <input
                required
                value={form.usuario}
                onChange={event =>
                  setForm({
                    ...form,
                    usuario: event.target.value
                  })
                }
                className={`${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } w-full border rounded-lg px-4 py-2.5`}
              />
            </div>

            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                E-mail
              </label>

              <input
                required
                type="email"
                value={form.email}
                onChange={event =>
                  setForm({
                    ...form,
                    email: event.target.value
                  })
                }
                className={`${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } w-full border rounded-lg px-4 py-2.5`}
              />
            </div>

            <div>
              <label className={`${text} block text-sm font-medium mb-1`}>
                Perfil
              </label>

              <select
                value={form.perfil}
                disabled={editando?.perfil === 'master'}
                onChange={event =>
                  setForm({
                    ...form,
                    perfil: event.target.value
                  })
                }
                className={`${
                  darkMode
                    ? 'bg-gray-900 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } w-full border rounded-lg px-4 py-2.5 disabled:opacity-60`}
              >
                <option value="operador">Operador</option>
                <option value="administrador">Administrador</option>
                {editando?.perfil === 'master' && (
                  <option value="master">Master</option>
                )}
              </select>
            </div>

            {!editando && (
              <div className="md:col-span-2">
                <label className={`${text} block text-sm font-medium mb-1`}>
                  Senha inicial
                </label>

                <input
                  required
                  type="password"
                  minLength={8}
                  value={form.senha}
                  onChange={event =>
                    setForm({
                      ...form,
                      senha: event.target.value
                    })
                  }
                  placeholder="Mínimo de 8 caracteres"
                  className={`${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } w-full border rounded-lg px-4 py-2.5`}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={limparFormulario}
              className={`${muted} px-4 py-2 rounded-lg border border-gray-600`}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={salvando}
              className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {salvando
                ? 'Salvando...'
                : editando
                  ? 'Salvar alterações'
                  : 'Cadastrar usuário'}
            </button>
          </div>
        </form>
      )}

      <div className={`${card} border rounded-xl overflow-hidden shadow-sm`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={
              darkMode
                ? 'bg-gray-900 text-gray-400'
                : 'bg-gray-50 text-gray-600'
            }>
              <tr className="text-left text-sm">
                <th className="px-5 py-4">Usuário</th>
                <th className="px-5 py-4">E-mail</th>
                <th className="px-5 py-4">Perfil</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Último acesso</th>
                <th className="px-5 py-4 text-right">Ações</th>
              </tr>
            </thead>

            <tbody>
              {usuarios.map(usuario => {
                const master =
                  usuario.perfil === 'master';

                return (
                  <tr
                    key={usuario.id}
                    className={
                      `${
                        darkMode
                          ? 'border-gray-700 hover:bg-gray-700/50'
                          : 'border-gray-200 hover:bg-gray-50'
                      } border-t`
                    }
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className={
                          `${
                            master
                              ? 'bg-purple-600'
                              : usuario.ativo
                                ? 'bg-blue-600'
                                : 'bg-gray-600'
                          } w-10 h-10 rounded-lg text-white flex items-center justify-center`
                        }>
                          {usuario.ativo
                            ? <UserCheck size={20} />
                            : <UserX size={20} />}
                        </div>

                        <div>
                          <div className={`${text} font-semibold`}>
                            {usuario.nome}
                          </div>

                          <div className={`${muted} text-xs mt-1`}>
                            @{usuario.usuario}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className={`${muted} px-5 py-4 text-sm`}>
                      {usuario.email}
                    </td>

                    <td className="px-5 py-4">
                      <span className={
                        `${
                          master
                            ? 'bg-purple-100 text-purple-800'
                            : usuario.perfil === 'administrador'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                        } inline-flex px-2.5 py-1 rounded-full text-xs font-medium`
                      }>
                        {master
                          ? 'Master'
                          : usuario.perfil === 'administrador'
                            ? 'Administrador'
                            : 'Operador'}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      <span className={
                        `${
                          usuario.ativo
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        } inline-flex px-2.5 py-1 rounded-full text-xs font-medium`
                      }>
                        {usuario.ativo
                          ? 'Ativo'
                          : 'Desativado'}
                      </span>
                    </td>

                    <td className={`${muted} px-5 py-4 text-sm`}>
                      {usuario.last_login_at
                        ? new Date(
                            usuario.last_login_at
                          ).toLocaleString('pt-BR')
                        : 'Nunca acessou'}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => abrirEdicao(usuario)}
                          title="Editar usuário"
                          className="p-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          <Edit3 size={16} />
                        </button>

                        <button
                          onClick={() => trocarSenha(usuario)}
                          title="Alterar senha"
                          className="p-2 rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                        >
                          <KeyRound size={16} />
                        </button>

                        {!master && (
                          <>
                            <button
                              onClick={() =>
                                alterarStatus(usuario)
                              }
                              title={
                                usuario.ativo
                                  ? 'Desativar acesso'
                                  : 'Reativar acesso'
                              }
                              className={
                                `${
                                  usuario.ativo
                                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                } p-2 rounded-lg`
                              }
                            >
                              <Power size={16} />
                            </button>

                            <button
                              onClick={() =>
                                excluirUsuario(usuario)
                              }
                              title="Excluir definitivamente"
                              className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {usuarios.length === 0 && (
          <div className={`${muted} py-12 text-center`}>
            Nenhum usuário cadastrado.
          </div>
        )}
      </div>
    </div>
  );
}
